import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Text, Icon, IconText } from '@reservamos/elements';
import TicketSummaryCard from 'ui/atoms/TicketSummaryCard';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

const propTypes = {
  isReturnTrip: PropTypes.bool,
  originTerminal: PropTypes.string,
  destinationTerminal: PropTypes.string,
  date: PropTypes.string,
  dateFormat: PropTypes.string,
  time: PropTypes.string,
  passengers: PropTypes.arrayOf(PropTypes.object),
  avatarForTicket: PropTypes.func,
  onClickedTicket: PropTypes.func,
  selectedTicketKey: PropTypes.string,
  selectedTicketKeys: PropTypes.arrayOf(PropTypes.string),
  selectable: PropTypes.bool,
  action: PropTypes.node,
};

/**
 * TicketSummaryContainer component.
 *
 * Presents one trip leg: a header describing the leg and a responsive grid of its tickets.
 *
 * The header formats the date itself rather than expecting the store to hold a display string —
 * the exchange reducer passes the API's search date straight through — and omits the time and its
 * icon entirely when the payload carries none.
 *
 * Renders nothing when the leg has no tickets, so a caller can hand it a return leg unconditionally.
 *
 * A ticket may carry `status`, `statusLabel` and `badgeLabel` alongside its data. They are the
 * caller's answer about that ticket's state — which screen it is on decides what makes a ticket
 * uncancellable or already cancelled — and this component only forwards them.
 *
 * @param {object} props - Component props.
 * @param {boolean} [props.isReturnTrip] - Labels the leg as return rather than outbound.
 * @param {string} [props.originTerminal] - Origin terminal name.
 * @param {string} [props.destinationTerminal] - Destination terminal name.
 * @param {string} [props.date] - Trip date as held in the store.
 * @param {string} [props.dateFormat] - Format `date` arrives in; defaults to the exchange shape.
 * @param {string} [props.time] - Departure time; the header omits the block when absent.
 * @param {Array<object>} [props.passengers] - Tickets on this leg.
 * @param {Function} [props.avatarForTicket] - Resolves a ticket to its artwork; keeps assets outside.
 * @param {Function} [props.onClickedTicket] - Receives the ticket key when a card is toggled.
 * @param {string} [props.selectedTicketKey] - Selected key for single selection.
 * @param {Array<string>} [props.selectedTicketKeys] - Selected keys for multi selection.
 * @param {boolean} [props.selectable] - When false the cards are display-only.
 * @param {React.ReactNode} [props.action] - Rendered as the last cell of the ticket grid, beside
 *   the tickets themselves. The caller owns it: this component holds no navigation and no copy of
 *   its own, so a leg that needs no action simply passes none.
 * @returns {JSX.Element|null}
 */
const TicketSummaryContainer = ({
  isReturnTrip = false,
  originTerminal = '',
  destinationTerminal = '',
  date = '',
  dateFormat = 'DD-MM-YYYY',
  time = '',
  passengers = [],
  avatarForTicket,
  onClickedTicket,
  selectedTicketKey = '',
  selectedTicketKeys,
  selectable = true,
  action = null,
}) => {
  const features = useWhitelabelFeatures();
  const { t } = useTranslation('exchange');

  if (!passengers.length) return null;

  const routeLabel = [originTerminal, destinationTerminal].filter(Boolean).join(' - ');
  const tripLabel = t(`ticket_list.${isReturnTrip ? 'return_trip' : 'departure_trip'}`);
  const tripIconType = isReturnTrip ? 'BackArrow' : 'ArrowForward';

  const parsedDate = date ? moment(date, dateFormat, true) : null;
  const dateLabel = parsedDate && parsedDate.isValid() ? parsedDate.format('D MMMM') : date;

  const timeFormat = features.HOUR_FORMAT === '24' ? 'HH:mm' : 'hh:mm A';
  const parsedTime = time ? moment(time, ['HH:mm', 'HH:mm:ss', moment.ISO_8601]) : null;
  const timeLabel = parsedTime && parsedTime.isValid() ? parsedTime.format(timeFormat) : '';

  return (
    // No card of its own: the leg header and the tickets are each their own surface, sitting
    // directly on the page. Wrapping them in a second bordered box drew a frame the design has not
    // got, and pushed the tickets a padding's worth away from the edge they should line up with.
    <div className="space-y-3 max-w-[600px]">
      <div className="flex flex-col gap-3 bg-white p-1 shadow-sm rounded-2xl md:flex-row md:flex-nowrap md:items-center md:gap-4 md:rounded-full">
        <div className="flex shrink-0 items-center gap-2 self-start rounded-full bg-primary-regular/10 px-2 py-1 md:self-auto">
          <Text color="primary" elementType="span" size="S" weight="semibold">
            {tripLabel}
          </Text>
          <Icon marginSide="left" size="S" type={tripIconType} />
        </div>

        {Boolean(routeLabel) && (
          <div className="min-w-0 w-full md:w-auto">
            <IconText iconType="Pin" fontSize="S" iconSize="S" label={routeLabel} />
          </div>
        )}

        <div className="flex w-full flex-row items-center justify-between gap-2 md:contents">
          {Boolean(dateLabel) && (
            <IconText iconType="CalendarOutlined" fontSize="S" iconSize="S" label={dateLabel} />
          )}
          {Boolean(timeLabel) && (
            <IconText iconType="Clock" fontSize="S" iconSize="S" label={timeLabel} />
          )}
          <IconText
            iconType="UserOutline"
            fontSize="S"
            iconSize="S"
            label={String(passengers.length)}
          />
        </div>
      </div>

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        {passengers.map((ticket, index) => {
          const ticketKeyValue = String(ticket.transporterKey ?? '');
          const spanLastRowFull =
            passengers.length > 1 && passengers.length % 2 === 1 && index === passengers.length - 1;
          const userName =
            ticket.userName ||
            `${ticket.firstName || ''} ${ticket.lastName || ''} ${
              ticket.secondLastName || ''
            }`.trim();

          return (
            <div
              key={ticketKeyValue || `ticket-${index}`}
              className={spanLastRowFull ? 'min-w-0 md:col-span-2' : 'min-w-0'}
            >
              <TicketSummaryCard
                userName={userName}
                floorNumber={ticket.floorNumber ?? ticket.seatLevel ?? ''}
                seatNumber={ticket.seatNumber ?? ''}
                serviceName={ticket.serviceName || ticket.serviceTypeProvider || ''}
                ticketNumber={ticket.tag || ticket.ticketNumber || ticketKeyValue}
                status={ticket.status || 'default'}
                ticketKey={ticketKeyValue}
                avatarSrc={avatarForTicket ? avatarForTicket(ticket) : ''}
                selectable={selectable}
                onClickedTicket={onClickedTicket}
                selectedTicketKey={selectedTicketKey}
                selectedTicketKeys={selectedTicketKeys}
                statusLabel={ticket.statusLabel || ''}
                badgeLabel={ticket.badgeLabel || ''}
              />
            </div>
          );
        })}

        {action}
      </div>
    </div>
  );
};

TicketSummaryContainer.propTypes = propTypes;

export default TicketSummaryContainer;
